import React from 'react';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import { LINKAGE_DATA } from '../data/mockData';
import { ArrowRight, ClipboardCheck, ShieldCheck, FileSignature, Sparkles, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

const MODULE_ICON = {
  pcc: ClipboardCheck,
  fraud: ShieldCheck,
  dlc: FileSignature,
};

const MODULE_COLOR = {
  pcc: 'from-amber-50 to-amber-100 border-amber-200 text-amber-700',
  fraud: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700',
  dlc: 'from-slate-50 to-slate-100 border-slate-200 text-slate-700',
};

export default function LinkagePage() {
  return (
    <div>
      <TopBar
        title="Risk → Compliance Linkage"
        subtitle="How a single risk cascades into compliance, fraud and DLC obligations"
        breadcrumbs={['HGS', 'Linkage View', LINKAGE_DATA.risk.id]}
      />

      <div className="p-8">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Top — the source risk */}
          <div className="p-6 bg-gradient-to-br from-red-50/60 to-white border-b border-slate-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#CC0000] to-[#990000] flex items-center justify-center shadow-md flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-slate-500">{LINKAGE_DATA.risk.id}</span>
                  <StatusBadge status="In Progress" />
                  <StatusBadge status={LINKAGE_DATA.risk.score} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">{LINKAGE_DATA.risk.name}</h2>
                <p className="text-sm text-slate-500 mt-1">Category: {LINKAGE_DATA.risk.category}</p>
              </div>
            </div>
          </div>

          {/* The fan-out visualization */}
          <div className="p-8">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#CC0000]" />
              <h3 className="text-sm font-bold text-slate-900">This risk requires action across 3 modules</h3>
            </div>

            <div className="hidden lg:flex items-stretch gap-4">
              {/* Source node */}
              <div className="w-56 flex-shrink-0 flex items-center">
                <div className="w-full p-4 rounded-xl bg-slate-900 text-white shadow-lg">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Source Risk</div>
                  <div className="text-sm font-bold leading-snug">{LINKAGE_DATA.risk.id}</div>
                  <div className="text-[11px] text-slate-300 mt-1 leading-snug">Raw material shortage</div>
                </div>
              </div>

              {/* Connector */}
              <div className="flex-1 relative">
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                  <path d="M0,50 C30,50 30,16 100,16" stroke="#CC0000" strokeWidth="0.6" fill="none" strokeDasharray="1.5,1.5" />
                  <path d="M0,50 C30,50 30,50 100,50" stroke="#CC0000" strokeWidth="0.6" fill="none" strokeDasharray="1.5,1.5" />
                  <path d="M0,50 C30,50 30,84 100,84" stroke="#CC0000" strokeWidth="0.6" fill="none" strokeDasharray="1.5,1.5" />
                </svg>
              </div>

              {/* Target nodes */}
              <div className="flex flex-col gap-3 w-96 flex-shrink-0">
                {LINKAGE_DATA.links.map((link) => {
                  const Icon = MODULE_ICON[link.moduleKey];
                  return (
                    <div key={link.module} className={cn('p-4 rounded-xl border bg-gradient-to-br', MODULE_COLOR[link.moduleKey])}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{link.module}</div>
                            <div className="text-sm font-bold text-slate-900">{link.item}</div>
                          </div>
                        </div>
                        <StatusBadge status={link.status} />
                      </div>
                      <p className="text-[11px] text-slate-700 leading-relaxed mb-2">{link.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-600 font-medium">Owner: {link.owner}</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-20 h-1.5 rounded-full bg-white/60 overflow-hidden">
                            <div className="h-full rounded-full bg-slate-900" style={{ width: `${link.progress}%` }} />
                          </div>
                          <span className="text-[10px] font-bold text-slate-700">{link.progress}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile fallback */}
            <div className="lg:hidden space-y-4">
              <div className="p-4 rounded-xl bg-slate-900 text-white">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Source Risk</div>
                <div className="text-sm font-bold mt-1">{LINKAGE_DATA.risk.name}</div>
              </div>
              {LINKAGE_DATA.links.map((link) => {
                const Icon = MODULE_ICON[link.moduleKey];
                return (
                  <div key={link.module} className="flex items-start gap-3">
                    <ArrowRight className="w-4 h-4 text-[#CC0000] mt-3 flex-shrink-0" />
                    <div className={cn('flex-1 p-4 rounded-xl border bg-gradient-to-br', MODULE_COLOR[link.moduleKey])}>
                      <div className="flex items-center gap-2"><Icon className="w-4 h-4" /> <span className="text-xs font-bold">{link.module}</span> <StatusBadge status={link.status} /></div>
                      <div className="text-sm font-bold text-slate-900 mt-1">{link.item}</div>
                      <p className="text-xs text-slate-700 mt-1">{link.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary footer */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-900">3 linked items</span> · 1 Green, 1 Amber, 1 Pending
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status="Green" />
              <StatusBadge status="Amber" />
              <StatusBadge status="Pending" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
